import { moveApi } from '../../services/moveApi'

const AUTH_STORAGE = {
  token: 'token',
  user: 'user',
  moveProfile: 'move_profile'
}

export default {
  namespaced: true,

  state: () => ({
    token: localStorage.getItem(AUTH_STORAGE.token) || null,
    user: JSON.parse(localStorage.getItem(AUTH_STORAGE.user) || 'null'),
    moveProfile: JSON.parse(localStorage.getItem(AUTH_STORAGE.moveProfile) || 'null'),
    productionProfile: JSON.parse(localStorage.getItem('production_profile') || 'null'),
    loading: false,
    error: null,
    initialized: false
  }),

  getters: {
    isAuthenticated: (state) => !!state.token,
    isMoveAdmin: (state) => state.moveProfile?.role === 'admin',
    hasProductionAccess: (state, getters) => {
      if (state.productionProfile?.has_access) return true
      if (getters.isMoveAdmin) return true
      return String(state.user?.email || '').toLowerCase() === 'peter@tiye.services'
    },
    isProductionAdmin: (state, getters) => {
      if (state.productionProfile?.is_production_admin) return true
      if (getters.isMoveAdmin) return true
      return String(state.user?.email || '').toLowerCase() === 'peter@tiye.services'
    },
    currentUserId: (state) => state.user?.id,
    displayName: (state) => {
      if (!state.user) return ''
      return [state.user.name, state.user.surname].filter(Boolean).join(' ').trim()
    }
  },

  mutations: {
    setLoading (state, value) {
      state.loading = value
    },
    setError (state, value) {
      state.error = value
    },
    setAuth (state, { token, user, moveProfile, productionProfile }) {
      state.token = token
      state.user = user
      state.moveProfile = moveProfile
      state.productionProfile = productionProfile || null
      localStorage.setItem(AUTH_STORAGE.token, token)
      localStorage.setItem(AUTH_STORAGE.user, JSON.stringify(user))
      localStorage.setItem(AUTH_STORAGE.moveProfile, JSON.stringify(moveProfile))
      if (productionProfile) {
        localStorage.setItem('production_profile', JSON.stringify(productionProfile))
      } else {
        localStorage.removeItem('production_profile')
      }
    },
    setUser (state, user) {
      state.user = user
      localStorage.setItem(AUTH_STORAGE.user, JSON.stringify(user))
    },
    setMoveProfile (state, moveProfile) {
      state.moveProfile = moveProfile
      localStorage.setItem(AUTH_STORAGE.moveProfile, JSON.stringify(moveProfile))
    },
    setProductionProfile (state, productionProfile) {
      state.productionProfile = productionProfile
      if (productionProfile) {
        localStorage.setItem('production_profile', JSON.stringify(productionProfile))
      } else {
        localStorage.removeItem('production_profile')
      }
    },
    clearAuth (state) {
      state.token = null
      state.user = null
      state.moveProfile = null
      state.productionProfile = null
      state.error = null
      localStorage.removeItem(AUTH_STORAGE.token)
      localStorage.removeItem(AUTH_STORAGE.user)
      localStorage.removeItem(AUTH_STORAGE.moveProfile)
      localStorage.removeItem('production_profile')
    },
    setInitialized (state, value) {
      state.initialized = value
    }
  },

  actions: {
    async login ({ commit }, { email, password }) {
      commit('setLoading', true)
      commit('setError', null)
      try {
        const { data } = await moveApi.login(email, password)
        commit('setAuth', {
          token: data.access_token,
          user: data.user,
          moveProfile: data.move_profile,
          productionProfile: data.production_profile || null
        })
        return { success: true }
      } catch (error) {
        const message = error.response?.data?.message ||
          error.response?.data?.errors?.email?.[0] ||
          error.response?.data?.errors?.password?.[0] ||
          'Login failed'
        commit('setError', message)
        return { success: false, error: message }
      } finally {
        commit('setLoading', false)
      }
    },

    async fetchSession ({ commit, state }) {
      if (!state.token) {
        commit('setInitialized', true)
        return
      }
      try {
        const { data } = await moveApi.me()
        commit('setUser', data.user)
        commit('setMoveProfile', data.move_profile)
        commit('setProductionProfile', data.production_profile || null)
      } catch {
        commit('clearAuth')
      } finally {
        commit('setInitialized', true)
      }
    },

    async logout ({ commit }) {
      try {
        await moveApi.logout()
      } catch {
        // ignore
      }
      commit('clearAuth')
    },

    async updateProfile ({ commit }, payload) {
      const { data } = await moveApi.updateProfile(payload)
      commit('setUser', data.user || data)
      return { success: true }
    },

    async updateAvatar ({ commit }, file) {
      const { data } = await moveApi.updateAvatar(file)
      commit('setUser', data.user || data)
      return { success: true }
    }
  }
}
